/**
 * Phase 125 — Leaflet pin picker.
 *
 * Wraps react-leaflet to let an operator drop a draggable marker on the
 * city map for their venue/academy location.  Returns lat/lng to the
 * parent via `onChange`.
 *
 * UX doctrine: spatial emotional ownership — the operator places their
 * ground in the city, not just types a postal code.  No API key required;
 * tiles served by OpenStreetMap (community-native).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Default marker icons are broken in CRA bundling — re-set them from CDN.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const FALLBACK_CENTER = [12.9716, 77.5946]; // Bengaluru

function ClickAndDragHandler({ onChange }) {
  useMapEvents({
    click(e) { onChange({ lat: e.latlng.lat, lng: e.latlng.lng }); },
  });
  return null;
}

function FlyToPin({ pin }) {
  const map = useMap();
  useEffect(() => {
    if (pin?.lat && pin?.lng) {
      map.flyTo([pin.lat, pin.lng], 15, { duration: 0.6 });
    }
  }, [pin?.lat, pin?.lng, map]);
  return null;
}

/**
 * Inner MapContainer wrapper.  Lives behind a unique `key` so React
 * always creates a fresh DOM node — eliminates the React-18 StrictMode
 * "container is already initialized" Leaflet error.
 *
 * Also remembers the Leaflet map instance via `whenReady` so the parent
 * cleanup can call `.remove()` if React decides to teardown.
 */
function LeafletInner({ center, pin, onChange }) {
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  useEffect(() => {
    return () => {
      try {
        mapInstanceRef.current?.remove();
        mapInstanceRef.current = null;
      } catch (_e) { /* swallow */ }
    };
  }, []);
  return (
    <MapContainer
      center={center}
      zoom={pin?.lat ? 15 : 11}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
      whenReady={(e) => { mapInstanceRef.current = e?.target; }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <ClickAndDragHandler onChange={onChange} />
      <FlyToPin pin={pin} />
      {pin?.lat && pin?.lng && (
        <Marker
          position={[pin.lat, pin.lng]}
          draggable
          ref={markerRef}
          eventHandlers={{
            dragend: () => {
              const ll = markerRef.current?.getLatLng();
              if (ll) onChange({ lat: ll.lat, lng: ll.lng });
            },
          }}
        />
      )}
    </MapContainer>
  );
}

export default function ArenaxLeafletPicker({ value, onChange, height = 280 }) {
  const center = useMemo(() => {
    if (value?.lat && value?.lng) return [value.lat, value.lng];
    return FALLBACK_CENTER;
  }, [value?.lat, value?.lng]);
  // Gate render behind a single mounted tick AND use a stable unique key
  // so StrictMode's second mount creates a brand-new DOM container
  // (instead of reusing the one Leaflet already attached _leaflet_id to).
  const [mounted, setMounted] = useState(false);
  const [mountKey] = useState(() => Math.random().toString(36).slice(2));
  useEffect(() => { setMounted(true); }, []);

  return (
    <div
      className="w-full rounded-xl overflow-hidden border"
      style={{ height, borderColor: 'var(--ax-border-quiet)' }}
      data-testid="arenax-leaflet-picker"
    >
      {mounted && (
        <LeafletInner key={mountKey} center={center} pin={value} onChange={onChange} />
      )}
    </div>
  );
}
